import type { Message, ReasoningEntry, ReasoningStreamUpdate } from '../types/chat';
import { environment } from '../../../../environments/environment';
type ConnectionState = 'disconnected' | 'connecting' | 'connected';
import axios from 'axios';
interface GovAPIResponse {
  key: string;
  value: any;
}

// Supported languages with ISO 639-1 codes
export type SupportedLanguage =
  | 'english' | 'hindi' | 'bengali' | 'telugu' | 'marathi' | 'tamil'
  | 'gujarati' | 'kannada' | 'malayalam' | 'punjabi' | 'odia'
  | 'arabic' | 'spanish' | 'french' | 'german' | 'chinese' | 'russian'
  | 'japanese' | 'portuguese' | 'italian';

// Language to ISO 639-1 code mapping
const LANGUAGE_TO_ISO: Record<SupportedLanguage, string> = {
  english: 'en',
  hindi: 'hi',
  bengali: 'bn',
  telugu: 'te',
  marathi: 'mr',
  tamil: 'ta',
  gujarati: 'gu',
  kannada: 'kn',
  malayalam: 'ml',
  punjabi: 'pa',
  odia: 'or',
  arabic: 'ar',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  chinese: 'zh',
  russian: 'ru',
  japanese: 'ja',
  portuguese: 'pt',
  italian: 'it'
};

// Helper function to get ISO code from language name
const getLanguageCode = (language: SupportedLanguage): string => {
  return LANGUAGE_TO_ISO[language] || 'en';
};

export class ChatService {
  private wsRef: WebSocket | null = null;
  private audioContextRef: AudioContext | null = null;
  private streamRef: MediaStream | null = null;
  private processorRef: ScriptProcessorNode | null = null;
  private audioQueueRef: string[] = [];
  private isPlayingRef = false;
  private audioSourcesRef: AudioBufferSourceNode[] = [];
  private lastInterruptTimeRef = 0;
  private authToken: string | null = null;
  private language: SupportedLanguage = 'english';
  private connectionState: ConnectionState = 'disconnected';
  private onConnectionStateChange?: (state: ConnectionState) => void;
  private onIsQueryingGov?: (isQuerying: boolean) => void;
  private onAudioPlayingChange?: (isPlaying: boolean) => void;
  private onSourceLinksReceived?: (links: string[]) => void;
  private onFollowupQuestionsReceived?: (questions: string[]) => void;
  private onInteractionIdReceived?: (interactionId: string) => void;
  private onVisualizationReceived?: (visualization: any) => void;
  private onError?: (error: string) => void;
  private isMuted = false;


  private onReasoningUpdate?: (update: ReasoningStreamUpdate) => void;
  private reasoningSocketRef: WebSocket | null = null;
  private reasoningInteractionId: string | null = null;
  private reasoningReconnectAttempts = 0;
  private reasoningReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reasoningEntries: ReasoningEntry[] = [];
  private reasoningWsUrl?: string;
  private reasoningOrgCode?: string;
  private navigator?: (path: string) => void;

  // Generate unique interactionId in format: xxxx-xxxx-xxxx-xxxx-xxxx-xxxx
  private generateInteractionId(): string {
    const segments = [];
    for (let i = 0; i < 6; i++) {
      segments.push(Math.random().toString(36).substring(2, 6));
    }
    return segments.join('-');
  }

  private buildReasoningWsUrl(interactionId: string): string | null {
    if (!interactionId) {
      return null;
    }

    const params = new URLSearchParams({ interactionId });
    if (this.reasoningOrgCode) {
      params.set('orgCode', this.reasoningOrgCode);
    }
    if (this.campaignId) {
      params.set('campaignId', this.campaignId);
    }

    if (this.reasoningWsUrl) {
      try {
        let customUrl = this.reasoningWsUrl.trim();
        if (!/^[a-z]+:\/\//i.test(customUrl)) {
          customUrl = `ws://${customUrl.replace(/^\/+/, '')}`;
        }

        const url = new URL(customUrl);
        for (const [key, value] of params.entries()) {
          url.searchParams.set(key, value);
        }
        return url.toString();
      } catch (err) {
        console.error('ChatService: Invalid reasoningWsUrl provided. Falling back to apiBaseUrl.', err);
      }
    }

    if (!this.apiBaseUrl) {
      return null;
    }

    try {
      const base = new URL(this.apiBaseUrl);
      const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
      // return `${protocol}//${base.host}/ws/reasoning-logs?${params.toString()}`;
      return `${environment.reasoningWsUrl}/ws/reasoning-logs?${params.toString()}`;
    } catch (error) {
      try {
        const sanitized = this.apiBaseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
        const protocol = this.apiBaseUrl.startsWith('https') ? 'wss://' : 'ws://';
        // return `${protocol}${sanitized}/ws/reasoning-logs?${params.toString()}`;
        return `${environment.reasoningWsUrl}/ws/reasoning-logs?${params.toString()}`;
      } catch (err) {
        console.error('ChatService: Failed to construct reasoning websocket URL', err);
        return null;
      }
    }
  }

  private startReasoningStream(interactionId: string) {
    if (!interactionId) return;

    this.stopReasoningStream();

    const wsUrl = this.buildReasoningWsUrl(interactionId);
    if (!wsUrl) {
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId,
        reasonings: [],
        error: 'Reasoning stream URL is not configured.'
      });
      return;
    }

    console.log('ChatService: Starting reasoning stream', {
      interactionId,
      wsUrl
    });

    this.reasoningInteractionId = interactionId;
    this.reasoningEntries = [];
    this.reasoningReconnectAttempts = 0;

    // Notify listeners that a new reasoning stream is starting
    this.onReasoningUpdate?.({
      type: 'init',
      interactionId,
      reasonings: []
    });

    this.connectReasoningSocket(wsUrl);
  }

  private connectReasoningSocket(wsUrl?: string) {
    if (!this.reasoningInteractionId) return;

    const url = wsUrl || this.buildReasoningWsUrl(this.reasoningInteractionId);
    if (!url) {
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId: this.reasoningInteractionId,
        reasonings: this.reasoningEntries,
        error: 'Unable to resolve reasoning stream endpoint.'
      });
      return;
    }

    try {
      const socket = new WebSocket(url);
      this.reasoningSocketRef = socket;

      socket.onopen = () => {
        this.reasoningReconnectAttempts = 0;
        console.debug('ChatService: Connected to reasoning stream');
      };

      socket.onmessage = (event) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          console.log('ChatService: Reasoning stream message received', data);
          this.handleReasoningMessage(data);
        } catch (err) {
          console.error('ChatService: Failed to parse reasoning message', err, event.data);
        }
      };

      socket.onerror = (event) => {
        console.error('ChatService: Reasoning websocket error', event);
      };

      socket.onclose = (event) => {
        console.warn('ChatService: Reasoning websocket closed', event.reason || event.code);
        if (this.reasoningInteractionId) {
          this.scheduleReasoningReconnect();
        }
      };
    } catch (err) {
      console.error('ChatService: Failed to open reasoning websocket', err);
      if (this.reasoningInteractionId) {
        this.scheduleReasoningReconnect();
      }
    }
  }

  private handleReasoningMessage(payload: any) {
    if (!payload) return;

    const interactionId = payload.interactionId || this.reasoningInteractionId;
    if (!interactionId || interactionId !== this.reasoningInteractionId) {
      return;
    }

    const type: string = payload.type || 'update';
    console.log('ChatService: Processing reasoning payload', {
      type,
      interactionId,
      payload
    });

    if (type === 'error') {
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId,
        reasonings: this.reasoningEntries,
        error: payload.message || payload.error || 'Unknown reasoning stream error',
        raw: payload
      });
      return;
    }

    if (type === 'delete') {
      this.reasoningEntries = [];
      this.onReasoningUpdate?.({
        type: 'delete',
        interactionId,
        reasonings: [],
        raw: payload
      });
      return;
    }

    if (type === 'delta') {
      const appended: ReasoningEntry[] = payload.appendedReasonings || [];
      console.log('ChatService: Reasoning delta received', {
        interactionId,
        appendedCount: appended.length
      });
      if (appended.length > 0) {
        this.reasoningEntries = [...this.reasoningEntries, ...appended];
      }
      this.onReasoningUpdate?.({
        type: 'delta',
        interactionId,
        reasonings: [...this.reasoningEntries],
        appendedReasonings: appended,
        raw: payload
      });
      return;
    }

    const updatedReasonings: ReasoningEntry[] = payload.reasonings
      || payload.fullDocument?.reasonings
      || [];
    console.log('ChatService: Reasoning update received', {
      interactionId,
      totalEntries: updatedReasonings.length
    });

    this.reasoningEntries = [...updatedReasonings];

    this.onReasoningUpdate?.({
      type: type === 'snapshot' ? 'snapshot' : 'update',
      interactionId,
      reasonings: [...this.reasoningEntries],
      raw: payload
    });
  }

  private scheduleReasoningReconnect() {
    if (!this.reasoningInteractionId) {
      return;
    }

    if (this.reasoningReconnectAttempts >= 5) {
      console.error('ChatService: Max reasoning reconnection attempts reached');
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId: this.reasoningInteractionId,
        reasonings: [...this.reasoningEntries],
        error: 'Unable to reconnect to reasoning stream after multiple attempts.'
      });
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reasoningReconnectAttempts), 10000);
    this.reasoningReconnectAttempts += 1;

    if (this.reasoningReconnectTimer) {
      clearTimeout(this.reasoningReconnectTimer);
    }

    this.reasoningReconnectTimer = setTimeout(() => {
      this.connectReasoningSocket();
    }, delay);
  }

  private stopReasoningStream() {
    if (this.reasoningReconnectTimer) {
      clearTimeout(this.reasoningReconnectTimer);
      this.reasoningReconnectTimer = null;
    }

    if (this.reasoningSocketRef) {
      try {
        this.reasoningSocketRef.onclose = null;
        this.reasoningSocketRef.onmessage = null;
        this.reasoningSocketRef.onerror = null;
        this.reasoningSocketRef.onopen = null;
        this.reasoningSocketRef.close();
      } catch (error) {
        console.warn('ChatService: Error closing reasoning websocket', error);
      }
      this.reasoningSocketRef = null;
    }

    this.reasoningInteractionId = null;
    this.reasoningEntries = [];
    this.reasoningReconnectAttempts = 0;
  }

  // Configuration - fully customizable for any app
  private apiBaseUrl: string = `${environment.controlplaneUrl}`;
  private authEndpoint: string = '/auth/getToken';
  private queryEndpoint: string = '/agent/flow/execute';
  private authPayload: any = null;
  private campaignId: string = '68c922967bd65b291df0f5e0';
  private agentCode: string = 'QUERY_USER_FEEDBACK_AND_RESPOND';
  private customQueryParams: Record<string, any> = {};

  // Configure the service for your specific app

  configure(config: {
    baseUrl?: string;
    authEndpoint?: string;
    queryEndpoint?: string;
    authPayload?: any;
    campaignId?: string;
    agentCode?: string;
    language?: string[];
    customQueryParams?: Record<string, any>;
    reasoningWsUrl?: string;
    orgCode?: string;
  }) {
    if (config.baseUrl) this.apiBaseUrl = config.baseUrl;
    if (config.authEndpoint) this.authEndpoint = config.authEndpoint;
    if (config.queryEndpoint) this.queryEndpoint = config.queryEndpoint;
    if (config.authPayload) this.authPayload = config.authPayload;
    if (config.campaignId) this.campaignId = config.campaignId;
    if (config.agentCode) this.agentCode = config.agentCode;
    if (config.customQueryParams) this.customQueryParams = config.customQueryParams;
    if (config.reasoningWsUrl) this.reasoningWsUrl = config.reasoningWsUrl;
    if (config.orgCode) {
      this.reasoningOrgCode = config.orgCode;
    } else if (config.customQueryParams && typeof config.customQueryParams.orgCode === 'string') {
      this.reasoningOrgCode = config.customQueryParams.orgCode;
    }
    // Don't reset language when configuring - preserve current language setting
    console.log('ChatService configured for:', config);
    console.log('ChatService: Current language after configure:', this.language);
  }

  // Allow host app to inject existing JWT token
  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  setNavigator(navigator: (path: string) => void) {
    this.navigator = navigator;
  }


  // Query Maharashtra Government schemes
  async queryGovernmentSchemes(query: string): Promise<{ text: string; sourceLinks: string[]; followupQuestions: string[]; interactionId?: string; visualization?: any }> {
    if (!this.authToken) {
      return {
        text: "Sorry, I'm unable to access information at the moment. Please try again later.",
        sourceLinks: [],
        followupQuestions: [],
        interactionId: undefined
      };
    }

    const generatedInteractionId = this.generateInteractionId();
    console.log('Generated interactionId:', generatedInteractionId);

    // Start reasoning stream immediately using generated interactionId
    this.startReasoningStream(generatedInteractionId);
    this.onInteractionIdReceived?.(generatedInteractionId);

    try {
      console.log('Making API call with query:', query);
      console.log('ChatService: Current language property:', this.language);
      console.log('ChatService: Language type:', typeof this.language);
      const languageCode = getLanguageCode(this.language);
      console.log('ChatService: Language ISO code:', languageCode);
      console.log('ChatService: Language array being sent:', [languageCode]);
      console.log('ChatService: LANGUAGE_TO_ISO mapping check:', LANGUAGE_TO_ISO[this.language]);

      const response = await axios.post(
        `${this.apiBaseUrl}${this.queryEndpoint}`,
        {
          data: {
            query: query,
            campaignId: this.campaignId,
            ...(this.agentCode !== 'QUERY_GENERAL_DATASET_AND_RESPOND' && { language: [languageCode] }),
            ...this.customQueryParams
          },
          user: {
            interactionId: generatedInteractionId
          },
          agent: {
            code: this.agentCode
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = response.data;
      console.log('Government API response status:', response.status);
      console.log('Government API response data:', JSON.stringify(data, null, 2));



      if (data.status === 200 && data.data && data.data.length > 0) {
        console.log('Processing successful API response...');
        let responseText = '';
        let sourceLinks: string[] = [];
        let followupQuestions: string[] = [];
        let resolvedInteractionId: string | undefined = generatedInteractionId;
        let visualizationData: any = undefined;

        // Log all available keys in the response
        console.log('Available response keys:', data.data.map((item: GovAPIResponse) => item.key));

        // Extract main response
        const responseData = data.data.find((item: GovAPIResponse) => item.key === 'response');
        if (responseData?.value) {
          console.log('Found response data:', responseData.value.substring(0, 200) + '...');
          responseText = responseData.value;

          // Remove source links section from the response text if present
          // Handle both Marathi and English versions
          const sourcePatternMarathi = /\n*संदर्भ दस्तावेज:\s*\n[\s\S]*$/;
          const sourcePatternEnglish = /\n*Reference documents?:\s*\n[\s\S]*$/i;

          responseText = responseText.replace(sourcePatternMarathi, '');
          responseText = responseText.replace(sourcePatternEnglish, '');

          // Also remove any trailing URLs that might not be under a header
          responseText = responseText.replace(/\n*https?:\/\/[^\s]+$/g, '');

          // Clean up extra whitespace
          responseText = responseText.trim();
          console.log('Cleaned response text length:', responseText.length);
        } else {
          console.warn('No response data found in API response');
        }

        // Extract dataset for visualization (MULTICOLUMN objectType or nested in DICTIONARY)
        let datasetValue = null;

        // Try to find MULTICOLUMN type first
        const multicolumnData = data.data.find((item: any) =>
          item.key === 'dataset' && item.objectType === 'MULTICOLUMN'
        );

        if (multicolumnData?.value) {
          datasetValue = multicolumnData.value;
          console.log('Found MULTICOLUMN dataset:', datasetValue);
        } else {
          // Try to find dataset nested in DICTIONARY type
          const dictionaryData = data.data.find((item: any) => item.objectType === 'DICTIONARY');
          if (dictionaryData?.entries) {
            const datasetEntry = dictionaryData.entries.find((entry: any) => entry.key === 'dataset');
            if (datasetEntry?.value) {
              datasetValue = datasetEntry.value;
              console.log('Found DICTIONARY dataset:', datasetValue);
            }
          }
        }

        if (datasetValue) {
          console.log('Processing dataset for visualization:', datasetValue);

          // Determine visualization type based on 'visual' flag and 'dimension'
          let vizType: 'chart' | 'table' | 'heatmap' = 'table'; // Default to table

          if (datasetValue.visual === true) {
            // visual: true means show as chart/heatmap
            if (datasetValue.dimension === 2) {
              vizType = 'chart'; // 2D -> bar chart only
            } else if (datasetValue.dimension >= 3) {
              vizType = 'heatmap'; // 3D+ data -> heatmap
            } else {
              vizType = 'chart'; // Default to chart if dimension not specified
            }
          } else {
            // visual: false means show as table (any dimension)
            vizType = 'table';
          }

          console.log(`Visualization type determined: ${vizType} (visual: ${datasetValue.visual}, dimension: ${datasetValue.dimension})`);

          visualizationData = {
            type: vizType,
            data: {
              title: datasetValue.title || 'Data Visualization',
              headers: datasetValue.headers || [],
              dimension: datasetValue.dimension || 2,
              visual: datasetValue.visual,
              data: datasetValue.data || []
            }
          };
          console.log('Processed visualization data:', visualizationData);
        } else {
          console.log('No dataset found in response');
        }

        // Extract follow-up questions (store separately, don't append to text)
        const followupData = data.data.find((item: GovAPIResponse) => item.key === 'followupQuestions');
        if (followupData?.value && followupData.value.length > 0) {
          followupQuestions = followupData.value;
          console.log('Found follow-up questions:', followupQuestions.length, followupQuestions);
        } else {
          console.log('No follow-up questions found or empty array');
        }

        // Extract source links (don't append to text, they'll be shown separately)
        const sourceData = data.data.find((item: GovAPIResponse) => item.key === 'sourceLinks');
        if (sourceData?.value && sourceData.value.length > 0) {
          sourceLinks = sourceData.value;
          console.log('Found source links:', sourceLinks.length);
        } else {
          console.log('No source links found in response');
        }

        // Extract interaction ID for content generation
        const interactionData = data.data.find((item: GovAPIResponse) => item.key === 'interactionId');
        if (interactionData?.value) {
          resolvedInteractionId = interactionData.value;
          console.log('Found interaction ID:', resolvedInteractionId);
        } else {
          console.log('No interaction ID found in response');
        }

        console.log('Final response prepared. Text length:', responseText.length, 'Source links:', sourceLinks.length, 'Follow-up questions:', followupQuestions.length, 'Interaction ID:', resolvedInteractionId, 'Visualization:', !!visualizationData);

        // Close reasoning stream once we have the final response
        this.stopReasoningStream();

        return {
          text: responseText || "No information found for your query.",
          sourceLinks,
          followupQuestions,
          interactionId: resolvedInteractionId,
          visualization: visualizationData
        };
      }

      console.warn('API response status was not 200 or no data found:', data.status);
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId: generatedInteractionId,
        reasonings: [],
        error: 'No reasoning available for this interaction.'
      });
      this.stopReasoningStream();
      return {
        text: 'No information found for your query. Please try asking in different words.',
        sourceLinks: [],
        followupQuestions: [],
        interactionId: undefined
      };
    } catch (error: any) {
      console.error('Failed to query :', error);
      // Note: Navigation should be handled by the component using this service
      // if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
      //   // Component should handle navigation
      // }
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        if (this.navigator) {
          this.navigator('/login');
        } else if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error details:', errorMessage);
      this.onReasoningUpdate?.({
        type: 'error',
        interactionId: generatedInteractionId,
        reasonings: [],
        error: errorMessage
      });
      this.stopReasoningStream();

      return {
        text: "Sorry, I'm unable to access information at the moment. Please try again later.",
        sourceLinks: [],
        followupQuestions: [],
        interactionId: undefined
      };
    }
  }

  setLanguage(language: SupportedLanguage) {
    console.log('ChatService: Setting language to:', language);
    this.language = language;
    console.log('ChatService: Language set to:', this.language);
    console.log('ChatService: ISO code will be:', getLanguageCode(this.language));
  }

  setOnConnectionStateChange(callback: (state: ConnectionState) => void) {
    this.onConnectionStateChange = callback;
  }

  setOnIsQueryingGov(callback: (isQuerying: boolean) => void) {
    this.onIsQueryingGov = callback;
  }

  setOnAudioPlayingChange(callback: (isPlaying: boolean) => void) {
    this.onAudioPlayingChange = callback;
  }

  setOnSourceLinksReceived(callback: (links: string[]) => void) {
    this.onSourceLinksReceived = callback;
  }

  setOnFollowupQuestionsReceived(callback: (questions: string[]) => void) {
    this.onFollowupQuestionsReceived = callback;
  }

  setOnInteractionIdReceived(callback: (interactionId: string) => void) {
    this.onInteractionIdReceived = callback;
  }

  setOnVisualizationReceived(callback: (visualization: any) => void) {
    this.onVisualizationReceived = callback;
  }

  setOnError(callback: (error: string) => void) {
    this.onError = callback;
  }

  setOnReasoningUpdate(callback: (update: ReasoningStreamUpdate) => void) {
    this.onReasoningUpdate = callback;
  }

  private updateConnectionState(state: ConnectionState) {
    this.connectionState = state;
    this.onConnectionStateChange?.(state);
  }

  async connectToGemini(onMessageReceived: (message: string, sourceLinks?: string[]) => void) {
    try {
      this.updateConnectionState('connecting');

      // Authenticate with government API first
      const token = (localStorage.getItem('token'));
      this.authToken = token;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      this.streamRef = stream;

      const audioContext = new AudioContext({ sampleRate: 16000 });
      this.audioContextRef = audioContext;

      const apiKey = "AIzaSyARmXbAQyDPfT-VovuIJdjXrB8PYLbSqwo";
      const ws = new WebSocket(
        `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent?key=${apiKey}`
      );
      this.wsRef = ws;

      ws.onopen = () => {
        console.log('WebSocket connected, sending setup message...');
        const setupMessage = {
          setup: {
            model: 'models/gemini-2.0-flash-exp',
            generationConfig: {
              responseModalities: 'audio',
              speechConfig: {
                voiceConfig: {
                  prebuiltVoiceConfig: {
                    voiceName: 'Puck'
                  }
                }
              },
              temperature: 0.3,
              topP: 0.9,
              maxOutputTokens: 1000
            },
            systemInstruction: {
              parts: [
                {
                  text: `You are a Maharashtra assistant. Always respond in ${this.language === 'english' ? 'English' : this.language.charAt(0).toUpperCase() + this.language.slice(1)}. You MUST use the queryGovernmentSchemes function for every question except when asked about your name. Never respond without calling a function first.`
                }
              ]
            },
            tools: [
              {
                functionDeclarations: [
                  {
                    name: 'getUserName',
                    description: 'Get the user name. Use only when asked about name.',
                    parameters: {
                      type: 'object',
                      properties: {},
                      required: []
                    }
                  },
                  {
                    name: 'queryGovernmentSchemes',
                    description: 'Query government database. Use for ALL questions except name questions.',
                    parameters: {
                      type: 'object',
                      properties: {
                        query: {
                          type: 'string',
                          description: 'The user question'
                        }
                      },
                      required: ['query']
                    }
                  }
                ]
              }
            ]
          }
        };

        console.log('Setup message:', JSON.stringify(setupMessage, null, 2));
        ws.send(JSON.stringify(setupMessage));
      };

      ws.onmessage = async (event) => {
        try {
          let data = event.data;

          if (data instanceof Blob) {
            data = await data.text();
          }

          const response = JSON.parse(data);
          console.log('Received message:', response);

          if (response.setupComplete) {
            this.updateConnectionState('connected');
            this.startAudioStreaming(stream, audioContext, ws);
            return;
          }

          if (response.toolCall) {
            await this.handleToolCall(response.toolCall, ws, onMessageReceived);
          }

          if (response.serverContent?.modelTurn?.parts) {
            response.serverContent.modelTurn.parts.forEach((part: any) => {
              if (part.inlineData?.data) {
                this.audioQueueRef.push(part.inlineData.data);
                this.playAudioQueue();
              }
            });
          }

          if (response.error) {
            this.onError?.(response.error.message || 'Server error occurred');
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = () => {
        this.onError?.('Connection error occurred');
        this.updateConnectionState('disconnected');
      };

      ws.onclose = (event) => {
        if (event.code !== 1000) {
          this.onError?.(`Connection closed: ${event.reason || 'Unknown error'}`);
        }
        this.updateConnectionState('disconnected');
        this.stopRecording();
      };

    } catch (err) {
      this.onError?.(err instanceof Error ? err.message : 'Failed to connect');
      this.updateConnectionState('disconnected');
    }
  }

  private async handleToolCall(toolCall: any, ws: WebSocket, onMessageReceived: (message: string, sourceLinks?: string[]) => void) {
    const functionCalls = toolCall.functionCalls || [];
    console.log('Tool call received:', functionCalls);

    for (const call of functionCalls) {
      console.log('Processing function call:', call.name);
      if (call.name === 'getUserName') {
        const toolResponse = {
          toolResponse: {
            functionResponses: [
              {
                id: call.id,
                name: 'getUserName',
                response: {
                  name: 'Onkar'
                }
              }
            ]
          }
        };

        ws.send(JSON.stringify(toolResponse));
      } else if (call.name === 'queryGovernmentSchemes') {
        const query = call.args?.query || '';
        this.onIsQueryingGov?.(true);
        const governmentResponse = await this.queryGovernmentSchemes(query);
        this.onIsQueryingGov?.(false);

        // Notify about source links and follow-up questions (always call to update UI)
        console.log('[Voice] Sending source links to callback:', governmentResponse.sourceLinks.length);
        this.onSourceLinksReceived?.(governmentResponse.sourceLinks);

        console.log('[Voice] Sending follow-up questions to callback:', governmentResponse.followupQuestions.length, governmentResponse.followupQuestions);
        this.onFollowupQuestionsReceived?.(governmentResponse.followupQuestions);

        // Send interaction ID for content generation
        if (governmentResponse.interactionId) {
          console.log('[Voice] Sending interaction ID to callback:', governmentResponse.interactionId);
          this.onInteractionIdReceived?.(governmentResponse.interactionId);
        }

        const toolResponse = {
          toolResponse: {
            functionResponses: [
              {
                id: call.id,
                name: 'queryGovernmentSchemes',
                response: {
                  result: governmentResponse.text
                }
              }
            ]
          }
        };

        ws.send(JSON.stringify(toolResponse));
      }
    }
  }

  private startAudioStreaming(
    stream: MediaStream,
    audioContext: AudioContext,
    ws: WebSocket
  ) {
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    this.processorRef = processor;

    processor.onaudioprocess = (e) => {
      if (this.isMuted || ws.readyState !== WebSocket.OPEN) return;

      const inputData = e.inputBuffer.getChannelData(0);

      const rms = Math.sqrt(
        inputData.reduce((sum, val) => sum + val * val, 0) / inputData.length
      );

      const now = Date.now();
      if (rms > 0.05 && this.isPlayingRef && (now - this.lastInterruptTimeRef) > 1000) {
        this.lastInterruptTimeRef = now;
        this.stopAllAudio();
        this.audioQueueRef = [];
      }

      const pcmData = new Int16Array(inputData.length);

      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]));
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }

      const base64Audio = btoa(
        String.fromCharCode(...new Uint8Array(pcmData.buffer))
      );

      const message = {
        realtimeInput: {
          mediaChunks: [
            {
              mimeType: 'audio/pcm;rate=16000',
              data: base64Audio
            }
          ]
        }
      };

      ws.send(JSON.stringify(message));
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  }

  private async playAudioQueue() {
    if (this.isPlayingRef || this.audioQueueRef.length === 0) return;

    this.isPlayingRef = true;
    this.onAudioPlayingChange?.(true);

    while (this.audioQueueRef.length > 0) {
      const base64Audio = this.audioQueueRef.shift()!;
      await this.playAudioChunk(base64Audio);
    }

    this.isPlayingRef = false;
    this.onAudioPlayingChange?.(false);
  }

  private async playAudioChunk(base64Audio: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const pcmData = new Int16Array(bytes.buffer);
        const floatData = new Float32Array(pcmData.length);
        for (let i = 0; i < pcmData.length; i++) {
          floatData[i] = pcmData[i] / (pcmData[i] < 0 ? 0x8000 : 0x7FFF);
        }

        const playbackContext = new AudioContext({ sampleRate: 24000 });
        const audioBuffer = playbackContext.createBuffer(1, floatData.length, 24000);
        audioBuffer.getChannelData(0).set(floatData);

        const source = playbackContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(playbackContext.destination);
        this.audioSourcesRef.push(source);

        source.onended = () => {
          const index = this.audioSourcesRef.indexOf(source);
          if (index > -1) {
            this.audioSourcesRef.splice(index, 1);
          }
          playbackContext.close();
          resolve();
        };

        source.start();
      } catch (err) {
        console.error('Error playing audio chunk:', err);
        reject(err);
      }
    });
  }

  private stopAllAudio() {
    this.audioSourcesRef.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // Already stopped
      }
    });
    this.audioSourcesRef = [];
    this.isPlayingRef = false;
    this.onAudioPlayingChange?.(false);
  }

  private stopRecording() {
    if (this.processorRef) {
      this.processorRef.disconnect();
      this.processorRef = null;
    }
    if (this.streamRef) {
      this.streamRef.getTracks().forEach(track => track.stop());
      this.streamRef = null;
    }
    if (this.audioContextRef) {
      this.audioContextRef.close();
      this.audioContextRef = null;
    }
    if (this.wsRef) {
      this.wsRef.close();
      this.wsRef = null;
    }
    this.stopAllAudio();
    this.audioQueueRef = [];
  }

  disconnect() {
    this.stopRecording();
    this.stopReasoningStream();
    this.updateConnectionState('disconnected');
    this.isMuted = false;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  getConnectionState() {
    return this.connectionState;
  }

  // Method for text-based chat (non-voice)
  async sendMessage(
    message: string,
    onStream?: (chunk: string) => void
  ): Promise<string> {
    // Ensure we have a valid token before making API calls
    if (!this.authToken) {
      console.log('No auth token found, authenticating...');
      this.authToken = localStorage.getItem('token');

      if (!this.authToken) {
        const errorMsg = 'Sorry, I am unable to access information at the moment. Please try again.';

        if (onStream) {
          onStream(errorMsg);
        }
        return errorMsg;
      }
    }

    // For text-based chat, query the government API directly
    this.onIsQueryingGov?.(true);

    try {
      const response = await this.queryGovernmentSchemes(message);
      this.onIsQueryingGov?.(false);

      // Always call callbacks to update UI
      console.log('Sending source links to callback:', response.sourceLinks.length);
      this.onSourceLinksReceived?.(response.sourceLinks);

      console.log('Sending follow-up questions to callback:', response.followupQuestions.length, response.followupQuestions);
      this.onFollowupQuestionsReceived?.(response.followupQuestions);

      // Send interaction ID for content generation
      if (response.interactionId) {
        console.log('Sending interaction ID to callback:', response.interactionId);
        this.onInteractionIdReceived?.(response.interactionId);
      }

      // Send visualization data if present
      if (response.visualization) {
        console.log('Sending visualization data to callback:', response.visualization);
        this.onVisualizationReceived?.(response.visualization);
      }

      // Simulate streaming for better UX
      const words = response.text.split(' ');
      let currentResponse = '';
      let index = 0;

      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (index < words.length) {
            currentResponse += (index > 0 ? ' ' : '') + words[index];
            onStream?.(currentResponse);
            index++;
          } else {
            clearInterval(interval);
            resolve(currentResponse);
          }
        }, 50);
      });
    } catch (error) {
      this.onIsQueryingGov?.(false);
      console.error('Error in sendMessage:', error);

      const errorMsg = 'Sorry, there was an error processing your request. Please try again.';

      if (onStream) {
        onStream(errorMsg);
      }
      return errorMsg;
    }
  }
}
